import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { PageContainer, PageTitleBadge, StatCard } from "../components";
import { useTranslation } from "../context/LanguageContext";
import { FaChartLine } from "react-icons/fa";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsivePie } from "@nivo/pie";
import "../styles/common.css";
import "./Dashboard.css";

export default function Dashboard() {
  const { t } = useTranslation();

  
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  
  useEffect(() => {
    const fetchRapport = async () => {
      try {
        const response = await api.get("/api/rapport-cheptel/");
        setRapport(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Erreur Dashboard:", err);
        console.error("Détails:", {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          config: {
            url: err.config?.url,
            baseURL: err.config?.baseURL,
            method: err.config?.method,
          }
        });
        
        let errorMessage = t.errorLoadingDashboard;
        
        if (err.response) {
          // Le serveur a répondu avec un code d'erreur
          if (err.response.status === 401) {
            errorMessage = t.sessionExpired;
            setTimeout(() => navigate("/logout"), 2000);
          } else if (err.response.status === 403) {
            errorMessage = t.accessDenied;
          } else if (err.response.status === 404) {
            errorMessage = t.endpointNotFound;
          } else if (err.response.status >= 500) {
            errorMessage = t.serverError;
          } else {
            errorMessage = `${t.error} ${err.response.status}: ${err.response.statusText || errorMessage}`;
          }
        } else if (err.request) {
          // La requête a été faite mais aucune réponse n'a été reçue
          errorMessage = t.connectionError;
          console.error("Pas de réponse du serveur. URL configurée:", process.env.REACT_APP_API_URL || "https://api.kyngaoundaba.com");
        } else {
          // Une erreur s'est produite lors de la configuration de la requête
          errorMessage = `${t.configError}: ${err.message}`;
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchRapport();
  }, [navigate]);

  const dataCategorie = rapport?.repartition_categorie?.map((item) => ({
    id: item.categorie,
    value: item.total,
    label: item.categorie,
  })) || [];

  const dataSexe = rapport?.repartition_sexe?.map((item) => ({
    id: item.sexe === "M" ? t.males : t.females,
    value: item.total,
    label: item.sexe === "M" ? t.males : t.females,
  })) || [];

  const dataIndicateurs = rapport ? [
    { id: "Achat", value: rapport.taux_achat || 0 },
    { id: "Mortalité", value: rapport.taux_mortalite || 0 },
    { id: "Natalité", value: rapport.taux_natalite || 0 },
    { id: "Vente", value: rapport.taux_vente || 0 },
  ] : [];

  const dataEvolution = rapport?.historique_effectif_mensuel?.map((item) => ({
    x: item.mois,
    y: item.effectif,
  })) || [];

  // Thème moderne pour Nivo
  const nivoTheme = {
    background: "transparent",
    text: {
      fontFamily: "inherit",
      fontSize: 14,
      fill: "#475569",
      outlineWidth: 0,
      outlineColor: "transparent",
    },
    axis: {
      domain: {
        line: {
          stroke: "#e2e8f0",
          strokeWidth: 2,
        },
      },
      legend: {
        text: {
          fontSize: 16,
          fill: "#475569",
          fontFamily: "inherit",
          fontWeight: 700,
        },
      },
      ticks: {
        line: {
          stroke: "#cbd5e1",
          strokeWidth: 1,
        },
        text: {
          fontSize: 13,
          fill: "#64748b",
          fontFamily: "inherit",
        },
      },
    },
    grid: {
      line: {
        stroke: "#e2e8f0",
        strokeWidth: 1,
        strokeDasharray: "3 3",
      },
    },
    tooltip: {
      container: {
        background: "#ffffff",
        color: "#0f172a",
        fontSize: 14,
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)",
        border: "2px solid #e2e8f0",
        padding: "12px 16px",
      },
    },
    legends: {
      text: {
        fontSize: 13,
        fill: "#64748b",
        fontFamily: "inherit",
      },
    },
  };

  // Palette de couleurs mélangée : logo (bruns/beiges) + couleurs actuelles
  const colors = {
    pie: ["#8B4513", "#D2691E"], // Bruns du logo
    bar: ["#8B4513", "#10b981", "#ef4444", "#D2691E"], // Bruns + vert + rouge + orange
    category: ["#8B4513", "#10b981", "#D2691E", "#A0522D", "#8B7355", "#CD853F"], // Bruns variés + vert
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="loading-container">
          <p className="loading-text">{t.loadingDashboard}</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitleBadge title={t.error} icon={<FaChartLine />} />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <p style={{ marginTop: '16px', fontSize: '0.9rem', color: '#64748b' }}>
            {t.checkConsole}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              backgroundColor: '#8B6F47',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            {t.retry}
          </button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitleBadge title={t.welcomeDashboard} icon={<FaChartLine />} />

      {rapport && (
        <>
          {/* Section des indicateurs clés (KPI) */}
          <h2 className="dashboard-section-title">{t.statistics}</h2>
          <div className="kpi-grid">
            <StatCard 
              type="effectif-initial" 
              label={t.initialStock} 
              value={rapport.effectif_initial ?? 0} 
              variant="dashboard" 
              showIcon={false} 
              className="card-tall" 
            />
            <StatCard 
              type="naissance" 
              label={t.births} 
              value={rapport.naissances ?? 0} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="mortalite" 
              label={t.mortality} 
              value={rapport.deces ?? 0} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="achat" 
              label={t.purchases} 
              value={rapport.achats ?? 0} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="vente" 
              label={t.sales} 
              value={rapport.ventes ?? 0} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="zakat" 
              label={t.zakat} 
              value={rapport.zakat ?? 0} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="effectif-final" 
              label={t.finalStock} 
              value={rapport.effectif_fin ?? 0} 
              variant="dashboard" 
              showIcon={false} 
              className="card-tall" 
            />
            <StatCard 
              type="valeur" 
              label={t.estimatedValue} 
              value={`${rapport.valeur_totale_cheptel ?? 0} FCFA`} 
              variant="dashboard" 
              showIcon={false} 
              className="card-tall" 
            />
          </div>
          
          <h2 className="dashboard-section-title">{t.indicators}</h2>
          <div className="chart-container">
            <div className="chart-scroll-container">
              <div className="nivo-chart-wrapper">
                <ResponsiveBar
                data={dataIndicateurs}
                keys={["value"]}
                indexBy="id"
                layout="vertical"
                margin={{ top: 20, right: 80, bottom: 80, left: 60 }}
                padding={0.3}
                valueScale={{ type: "linear", min: 0, max: 100 }}
                indexScale={{ type: "band", round: true }}
                colors={(d) => {
                  const colorsMap = {
                    "Achat": "#3b82f6", // Bleu (cohérent avec Evenements)
                    "Mortalité": "#ef4444", // Rouge
                    "Natalité": "#10b981", // Vert
                    "Vente": "#f59e0b", // Orange (cohérent avec Evenements)
                  };
                  return colorsMap[d.id] || "#8b5cf6";
                }}
                borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Indicateurs",
                  legendPosition: "middle",
                  legendOffset: 60,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  format: (value) => `${value}%`,
                  legend: "Pourcentage (%)",
                  legendPosition: "middle",
                  legendOffset: -50,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                animate={true}
                motionConfig="gentle"
                theme={nivoTheme}
                tooltip={({ value }) => (
                  <div className="nivo-tooltip">
                    <strong>{value}%</strong>
                  </div>
                )}
                enableLabel={true}
                labelFormat={(value) => `${value}%`}
              />
              </div>
            </div>
          </div>
        

          <h2 className="dashboard-section-title">{t.monthlyEvolution}</h2>
          <div className="chart-container">
            <div className="chart-scroll-container">
              <div className="nivo-chart-wrapper">
                <ResponsiveLine
                data={[
                  {
                    id: "Effectif",
                    color: "#10b981",
                    data: dataEvolution,
                  },
                ]}
                margin={{ top: 50, right: 110, bottom: 80, left: 60 }}
                xScale={{ type: "point" }}
                yScale={{
                  type: "linear",
                  min: "auto",
                  max: "auto",
                  stacked: false,
                  reverse: false,
                }}
                curve="monotoneX"
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  orient: "bottom",
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: "Mois",
                  legendOffset: 60,
                  legendPosition: "middle",
                }}
                axisLeft={{
                  orient: "left",
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Effectif",
                  legendOffset: -50,
                  legendPosition: "middle",
                }}
                pointSize={8}
                pointColor={{ theme: "background" }}
                pointBorderWidth={2}
                pointBorderColor={{ from: "serieColor" }}
                pointLabelYOffset={-12}
                enableArea={true}
                areaOpacity={0.1}
                useMesh={true}
                theme={nivoTheme}
                colors={["#10b981"]}
                lineWidth={3}
                enablePointLabel={true}
                pointLabel={(d) => `${d.y}`}
                tooltip={({ point }) => (
                  <div className="nivo-tooltip">
                    <strong>{point.serieId}</strong>
                    <br />
                    {point.data.x}: <strong>{point.data.y}</strong>
                  </div>
                )}
              />
              </div>
            </div>
          </div>
        
          {/* Graphique par sexe */}
          <h2 className="dashboard-section-title">{t.sexDistribution}</h2>
          <div className="chart-container">
            <div className="chart-scroll-container">
              <div className="nivo-chart-wrapper pie-wrapper">
                <ResponsivePie
                data={dataSexe}
                margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                innerRadius={0.5}
                padAngle={2}
                cornerRadius={8}
                activeOuterRadiusOffset={8}
                colors={colors.pie}
                borderWidth={2}
                borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor="#64748b"
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: "color" }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#ffffff"
                theme={nivoTheme}
                tooltip={({ datum }) => (
                  <div className="nivo-tooltip">
                    <strong>{datum.label}</strong>
                    <br />
                    <strong>{datum.value}</strong> {t.animals}
                  </div>
                )}
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    justify: false,
                    translateX: 0,
                    translateY: 56,
                    itemsSpacing: 0,
                    itemWidth: 100,
                    itemHeight: 18,
                    itemTextColor: "#64748b",
                    itemDirection: "left-to-right",
                    itemOpacity: 1,
                    symbolSize: 18,
                    symbolShape: "circle",
                  },
                ]}
                animate={true}
                motionConfig="gentle"
              />
              </div>
            </div>
          </div>
      
          {/* Graphique par catégorie */}
          <h2 className="dashboard-section-title">{t.categoryDistribution}</h2>
          <div className="chart-container">
            <div className="chart-scroll-container">
              <div className="nivo-chart-wrapper">
                <ResponsiveBar
                data={dataCategorie}
                keys={["value"]}
                indexBy="id"
                margin={{ top: 50, right: 130, bottom: 80, left: 60 }}
                padding={0.3}
                valueScale={{ type: "linear" }}
                indexScale={{ type: "band", round: true }}
                colors={(d) => {
                  const index = dataCategorie.findIndex(item => item.id === d.id);
                  return colors.category[index % colors.category.length];
                }}
                borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: "Catégorie",
                  legendPosition: "middle",
                  legendOffset: 60,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: t.numberOfAnimals,
                  legendPosition: "middle",
                  legendOffset: -50,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
                animate={true}
                motionConfig="gentle"
                theme={nivoTheme}
                tooltip={({ value }) => (
                  <div className="nivo-tooltip">
                    <strong>{value}</strong> {t.animals}
                  </div>
                )}
                enableLabel={true}
              />
              </div>
            </div>
          </div>
        
        </>
      )}
    </PageContainer>
  );
}